import type { AbortSignal as SdkAbortSignal } from "@aws-sdk/types";
import { ResolveSearchConfigCommand } from "@amzn/ttechclaimintakeservice-client";

import { getAuthenticatedClient } from "src/api/setup/getAuthenticatedClient";
import type { LobKey } from "src/jsonForms/preScreen/types";

import { SEARCH_CONFIG_REGISTRY } from "./registry";

/**
 * The one module under src/claimsHistory/ that knows where a Search_Config
 * comes from, and the only one permitted to import from src/api/. It translates
 * the transport's failures into the three errors below and nothing else, so
 * keep validation, memoization, timeouts, and metrics out of here.
 */
export interface SearchConfigSource {
  /**
   * Resolves with an unvalidated document, or rejects with one of the errors
   * below, or with the transport's own `AbortError` when `signal` aborts.
   */
  load(lob: LobKey, signal?: AbortSignal): Promise<unknown>;
}

/** Thrown when no Search_Config exists for a line of business. */
export class SearchConfigMissingError extends Error {
  constructor(readonly lob: LobKey) {
    super(`No claims history configuration registered for "${lob}"`);
    this.name = "SearchConfigMissingError";
  }
}

/**
 * Why a retrieval that reached for a document came back without one:
 *
 * - `transport`: the request failed, or the session had no token to send.
 * - `unparseable`: a response arrived but carried nothing a validator could
 *   read. A document that is present but the wrong shape is not this; that
 *   reaches the validator and is reported as a rejected config.
 *
 * `timeout` is the third retrieval cause and is not raised here, because the
 * source does not own the clock. The resolver decides it from an abort reason.
 */
export type SearchConfigSourceFailureCause = "transport" | "unparseable";

/** Thrown when a retrieval failed for a reason another attempt could clear. */
export class SearchConfigRetrievalError extends Error {
  constructor(
    readonly retrievalCause: SearchConfigSourceFailureCause,
    message: string,
    readonly underlying?: unknown
  ) {
    super(message);
    this.name = "SearchConfigRetrievalError";
  }
}

/**
 * Read by name rather than `instanceof`: the generated client names each error
 * after the service's `x-amzn-ErrorType`, and a name survives a second copy of
 * the client package where a class identity would not.
 */
function errorName(error: unknown): string | undefined {
  return error instanceof Error ? error.name : undefined;
}

function translateTransportError(lob: LobKey, error: unknown): unknown {
  switch (errorName(error)) {
    // Passed through untouched. Whether it is a timeout or a navigation is
    // decided by whoever aborted, from the reason they gave, not from this.
    case "AbortError":
      return error;
    // The only error `ResolveSearchConfig` models. The service raises it for a
    // line of business it holds no document for.
    case "InvalidInputException":
      return new SearchConfigMissingError(lob);
    // A body the client could not deserialize, such as a gateway's HTML page.
    case "SyntaxError":
      return new SearchConfigRetrievalError(
        "unparseable",
        `The ${lob} search configuration response could not be read.`,
        error
      );
    default:
      return new SearchConfigRetrievalError(
        "transport",
        `The ${lob} search configuration could not be retrieved.`,
        error
      );
  }
}

/**
 * Fetches the document from `POST /resolveSearchConfig`. The response's
 * `searchConfig` is returned as `unknown` on purpose, so a served document goes
 * through `validateSearchConfig` exactly as a hand-written one would.
 *
 * The response's `searchConfigId` identifies which version of the stored
 * document was served. It is not logged, since the UI has no logger and
 * production code does not write to the console; it stays readable on the
 * response in the browser's network panel, which is where someone asking why
 * the columns changed will be looking.
 */
export const serviceSearchConfigSource: SearchConfigSource = {
  async load(lob, signal) {
    const entry = SEARCH_CONFIG_REGISTRY[lob];
    if (!entry) {
      throw new SearchConfigMissingError(lob);
    }

    const { client } = await getAuthenticatedClient();
    if (!client) {
      throw new SearchConfigRetrievalError(
        "transport",
        `The ${lob} search configuration could not be requested: no session token is available.`
      );
    }

    let response;
    try {
      response = await client.send(
        new ResolveSearchConfigCommand({ lineOfBusiness: entry.lineOfBusiness }),
        { abortSignal: signal as unknown as SdkAbortSignal }
      );
    } catch (error) {
      throw translateTransportError(lob, error);
    }

    if (response.searchConfig === undefined || response.searchConfig === null) {
      throw new SearchConfigRetrievalError(
        "unparseable",
        `The ${lob} search configuration response carried no document.`
      );
    }
    return response.searchConfig as unknown;
  },
};

export const searchConfigSource: SearchConfigSource = serviceSearchConfigSource;
